import { Controller } from "@hotwired/stimulus"

// Salesforce Status Controller - Real-time sync status updates
export default class extends Controller {
  static targets = ["details"]
  static values = {
    applicationId: String,
    poll: Boolean,
    pollInterval: { type: Number, default: 3000 } // 3 seconds
  }

  connect() {
    console.log("Salesforce status controller connected")
    
    // Start polling if sync is pending
    if (this.pollValue) {
      this.startPolling()
    }

    // Listen for Turbo Stream updates
    this.boundUpdateHandler = this.handleSalesforceUpdate.bind(this)
    document.addEventListener("salesforce:updated", this.boundUpdateHandler)
  }

  disconnect() {
    this.stopPolling()
    if (this.boundUpdateHandler) {
      document.removeEventListener("salesforce:updated", this.boundUpdateHandler)
    }
  }

  // Start polling for status updates
  startPolling() {
    if (this.pollingTimer) return
    
    console.log(`Starting Salesforce status polling for application ${this.applicationIdValue}`)
    
    this.pollingTimer = setInterval(() => {
      this.pollStatus()
    }, this.pollIntervalValue)
  }

  // Stop polling
  stopPolling() {
    if (this.pollingTimer) {
      console.log("Stopping Salesforce status polling")
      clearInterval(this.pollingTimer)
      this.pollingTimer = null
    }
  }

  // Poll the server for status updates
  async pollStatus() {
    try {
      const response = await fetch(`/admin/salesforce/status/${this.applicationIdValue}`, {
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const data = await response.json()
        this.updateFromData(data)
        
        // Stop polling if sync is complete or failed
        if (data.status !== 'pending') {
          this.stopPolling()
          this.pollValue = false
        }
      }
    } catch (error) {
      console.error("Error polling Salesforce status:", error)
      // Continue polling on error, but maybe reduce frequency
      this.pollIntervalValue = Math.min(this.pollIntervalValue * 1.5, 30000) // Max 30 seconds
    }
  }

  // Update status from server data
  updateFromData(data) {
    const { status, leadId, syncedAt, error, lastSyncDisplay } = data
    
    // Update the component based on new status
    this.updateStatusDisplay(status, {
      leadId,
      syncedAt,
      error,
      lastSyncDisplay
    })

    // Trigger custom event for other components
    this.dispatch("updated", {
      detail: { applicationId: this.applicationIdValue, status, data }
    })
  }

  // Update the visual status display
  updateStatusDisplay(status, details = {}) {
    const element = this.element
    
    // Remove all status classes
    element.classList.remove(
      'bg-green-50', 'text-green-700', 'border-green-200',
      'bg-yellow-50', 'text-yellow-700', 'border-yellow-200',
      'bg-red-50', 'text-red-700', 'border-red-200',
      'bg-gray-50', 'text-gray-700', 'border-gray-200'
    )
    
    // Get status configuration
    const config = this.getStatusConfig(status, details)
    
    // Apply new classes
    element.classList.add(...config.classes.split(' '))
    
    // Update icon
    const icon = element.querySelector('svg')
    if (icon) {
      icon.classList.remove('animate-spin', 'text-green-500', 'text-yellow-500', 'text-red-500', 'text-gray-500')
      icon.classList.add(...config.iconClasses.split(' '))
      
      // Update icon path
      const path = icon.querySelector('path')
      if (path) {
        path.setAttribute('d', config.icon)
      }
    }
    
    // Update text content
    const textElement = element.querySelector('.font-medium')
    if (textElement) {
      textElement.textContent = config.text
    }
    
    // Update description
    const descElement = element.querySelector('.text-xs.opacity-75')
    if (descElement) {
      descElement.textContent = config.description
    }
    
    // Show/hide retry button
    const retryButton = element.querySelector('[data-action*="retrySync"]')
    if (retryButton) {
      retryButton.style.display = config.showRetry ? 'block' : 'none'
    }
    
    // Update lead ID display
    const leadIdElement = element.querySelector('.font-mono')
    if (leadIdElement && details.leadId) {
      leadIdElement.textContent = details.leadId.substring(0, 8)
    }

    // Add success animation for completed syncs
    if (status === 'synced') {
      this.showSuccessAnimation()
    }
  }

  // Get status configuration object
  getStatusConfig(status, details) {
    switch (status) {
      case 'synced':
        return {
          classes: 'bg-green-50 text-green-700 border-green-200',
          iconClasses: 'text-green-500',
          icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
          text: 'Synced to CRM',
          description: details.lastSyncDisplay || 'Successfully synced',
          showRetry: false
        }
      case 'pending':
        return {
          classes: 'bg-yellow-50 text-yellow-700 border-yellow-200',
          iconClasses: 'text-yellow-500 animate-spin',
          icon: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
          text: 'Syncing to CRM',
          description: 'Sync in progress...',
          showRetry: false
        }
      case 'failed':
        return {
          classes: 'bg-red-50 text-red-700 border-red-200',
          iconClasses: 'text-red-500',
          icon: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.96-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z',
          text: 'Sync Failed',
          description: details.error?.substring(0, 50) || 'Unable to sync to CRM',
          showRetry: true
        }
      default:
        return {
          classes: 'bg-gray-50 text-gray-700 border-gray-200',
          iconClasses: 'text-gray-500',
          icon: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
          text: 'Not Synced',
          description: 'Will sync when submitted',
          showRetry: false
        }
    }
  }

  // Show success animation
  showSuccessAnimation() {
    const element = this.element
    element.classList.add('animate-pulse')
    
    setTimeout(() => {
      element.classList.remove('animate-pulse')
    }, 1000)
  }

  // Handle Turbo Stream updates
  handleSalesforceUpdate(event) {
    const { applicationId, status, data } = event.detail
    
    if (applicationId === this.applicationIdValue) {
      this.updateFromData({ status, ...data })
    }
  }

  // Manual retry sync
  async retrySync(event) {
    event.preventDefault()
    
    try {
      // Update to pending state immediately for user feedback
      this.updateStatusDisplay('pending')
      this.pollValue = true
      this.startPolling()
      
      const response = await fetch(`/admin/salesforce/retry/${this.applicationIdValue}`, {
        method: 'POST',
        headers: {
          "Accept": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      // Show success feedback
      this.showRetryInitiated()
      
    } catch (error) {
      console.error("Error retrying Salesforce sync:", error)
      this.showRetryError()
    }
  }

  // Show retry initiated feedback
  showRetryInitiated() {
    // Could show a toast notification or temporary message
    const originalText = this.element.querySelector('.font-medium').textContent
    const textElement = this.element.querySelector('.font-medium')
    
    textElement.textContent = 'Retry initiated...'
    
    setTimeout(() => {
      if (textElement.textContent === 'Retry initiated...') {
        textElement.textContent = originalText
      }
    }, 2000)
  }

  // Show retry error feedback
  showRetryError() {
    this.updateStatusDisplay('failed', { error: 'Retry failed. Please try again.' })
    this.stopPolling()
    this.pollValue = false
  }

  // Toggle details panel
  showDetails() {
    if (this.hasDetailsTarget) {
      this.detailsTarget.classList.remove('hidden')
    }
  }

  hideDetails() {
    if (this.hasDetailsTarget) {
      this.detailsTarget.classList.add('hidden')
    }
  }

  // Update status from external events (e.g., Turbo Streams)
  updateStatus(event) {
    const { status, data } = event.detail
    this.updateFromData({ status, ...data })
  }
}